import { TaskStatus } from './task-status';
import { TaskDate, TaskDatePrimitives } from './task-date';
import { LocalDate } from 'src/core/common/domain/date/local-date';
import { DateRange } from './date-range';
import { CloseTaskData } from 'src/app/components/tasks/close-task-modal/close-task-modal.component';
import { Attachment } from 'src/core/attachment/domain/attachment';
import { LocalFileAttachment } from 'src/core/attachment/domain/local-file-attachment';
import { Nullable } from 'src/core/common/domain/types/types';
import { DateHelper } from 'src/core/common/domain/date/date-helper';
import { TimezoneHint } from 'src/core/common/domain/date/timezone-hint';
import { TaskAmbit, TaskAmbitData } from './task-ambit';
import { StringHelper } from 'src/core/common/domain/strings/string-helper';
import { UserInfo } from 'src/core/authentication/domain/user-info';
import { LocalAttachment } from 'src/core/attachment/domain/local-attachment';
import { Ambit } from './ambit';
import { Resolution } from './resolution';
import { DeadlineFilter } from './filters';
import { RejectTaskData } from 'src/app/components/tasks/reject-task-modal/reject-task-modal.component';
import { Subject } from 'rxjs';
import { WorkType, WorkTypePrimitives } from './work-type';
import { Priority, PriorityPrimitives } from './priority';

export type TaskType = {
  id: string;
  name: string;
};

export type Route = {
  id: string;
  name: string;
};

export type Assignee = {
  id: string;
  username?: Nullable<string>;
  name: string;
  email: string;
};

export type Location = {
  full_address: string;
  geolocation?: {
    latitude: number;
    longitude: number;
  };
};

export type ConfirmCloseTaskData = {
  resolution?: Nullable<string>;
  observationsForClient?: Nullable<string>;
};

export type ConfirmCloseTaskDataPrimitives = {
  resolution?: Nullable<string>;
  observations_for_client?: Nullable<string>;
};

export type ConfirmRejectTaskData = {
  resolution?: Nullable<string>;
  observationsForClient?: Nullable<string>;
};

export type ConfirmRejectTaskDataPrimitives = {
  resolution?: Nullable<string>;
  observations_for_client?: Nullable<string>;
};

export type DescriptionFilePrimitives = {
  id: string;
  filename: string;
  url: string;
};

// Definition of object stored in Storage.
// If it changed, it should be handled to prevent corruption of data
export type TaskPrimitives = {
  id: string;
  status: string;
  subject: string;
  description: string;
  description_files?: Array<DescriptionFilePrimitives>;
  in_final_state: boolean;
  observations_for_client: string;
  work_type: WorkTypePrimitives;
  task_type: Nullable<{
    id: string;
    name: string;
  }>;
  // Optional to stay backward-compatible with task records stored before priority existed.
  priority?: Nullable<PriorityPrimitives>;
  // Optional to stay backward-compatible with task records stored before route existed.
  route?: Nullable<{
    id: string;
    name: string;
  }>;
  ambit: TaskAmbitData;
  date: TaskDatePrimitives;
  assignee: {
    id: string;
    username?: Nullable<string>;
    name: string;
    email: string;
  };
  frequency: Nullable<string>;
  resolution: Nullable<{
    id: string;
    name: string;
  }>;
  must_be_modified_by_assignee: boolean;
  location: {
    full_address: Nullable<string>;
    geolocation?: { latitude: number; longitude: number };
  };
  started_at: Nullable<string>;
  updated_at: Nullable<string>;
  closed_at: Nullable<string>;
  confirm_closed_at: Nullable<string>;
  rejected_at: Nullable<string>;
  confirm_rejected_at: Nullable<string>;
  created_at: Nullable<string>;
  attachments: Array<{
    id: string;
    filename: string;
    path: string;
    size: number;
  }>;
  display_service_address_in_task: boolean;
  display_postal_address_in_immediate_task: boolean;
  display_postal_address_in_planned_task: boolean;
  display_ambit_in_immediate_task: boolean;
  display_ambit_in_planned_task: boolean;
  display_description_in_immediate_task: boolean;
  display_description_in_planned_task: boolean;
  display_inline_observations_for_client: boolean;
  close_task_data: {
    predefinedObservations?: Nullable<string>;
    resolutions?: Nullable<string>;
    observations?: Nullable<string>;
    emails?: Nullable<string>;
    signatureType?: Nullable<string>;
    signature?: Nullable<{
      id: string;
      filename: string;
      path: string;
      size: number;
    }>;
  };
  reject_task_data: {
    predefinedObservations?: Nullable<string>;
    resolutions?: Nullable<string>;
    observations?: Nullable<string>;
  };
  confirm_close_task_data: {
    observations_for_client?: Nullable<string>;
    resolution?: Nullable<string>;
  };
  confirm_reject_task_data: {
    observations_for_client?: Nullable<string>;
    resolution?: Nullable<string>;
  };
};

export class Task {
  static readonly LOCAL_ID_PREFIX: string = '--';

  searchableText = '';
  public taskHasChanged$ = new Subject<void>();
  private previousStatus: Nullable<TaskStatus> = null;
  private pathIds: string[];
  private closeTaskData: CloseTaskData;
  private rejectTaskData: RejectTaskData;
  private confirmCloseTaskData: ConfirmCloseTaskData;
  private confirmRejectTaskData: ConfirmRejectTaskData;

  // Precomputed numeric sort keys for the immutable criteria, calculated once at construction
  // so ordering large task lists only compares cached numbers.
  public readonly sortExpectedDay: number;
  public readonly sortExpectedStart: number;
  public readonly sortPriority: number;
  public readonly sortImmediateRank: number;

  constructor(
    public id: string,
    public status: TaskStatus,
    public subject: string,
    public readonly description: string,
    public readonly descriptionFiles: Array<DescriptionFilePrimitives>,
    public readonly workType: WorkType,
    public readonly taskType: Nullable<TaskType> = null,
    public readonly priority: Nullable<Priority> = null,
    public readonly route: Nullable<Route> = null,
    public readonly ambit: TaskAmbit,
    public readonly date: TaskDate,
    public readonly assignee: Nullable<Assignee>,
    public readonly mustBeModifiedByAssignee: boolean,
    public readonly location: Location,
    public readonly frequency: Nullable<string>,
    public resolution: Nullable<Resolution> = null,
    public startedAt: Nullable<LocalDate>,
    public updatedAt: Nullable<LocalDate>,
    public closedAt: Nullable<LocalDate>,
    public confirmClosedAt: Nullable<LocalDate>,
    public rejectedAt: Nullable<LocalDate>,
    public confirmRejectedAt: Nullable<LocalDate>,
    public attachments: Array<Attachment>,
    private displayServiceAddressInTask: boolean = false,
    private displayPostalAddressInImmediateTask: boolean = true,
    private displayPostalAddressInPlannedTask: boolean = true,
    private displayAmbitInImmediateTask: boolean = true,
    private displayAmbitInPlannedTask: boolean = true,
    private displayDescriptionInImmediateTask: boolean = true,
    private displayDescriptionInPlannedTask: boolean = true,
    private displayInlineObservationForClient: boolean = false,
    closeTaskData: Nullable<CloseTaskData>,
    rejectTaskData: Nullable<RejectTaskData>,
    public createdAt: Nullable<LocalDate> = null,
    public observationsForClient: Nullable<string> = null,
    confirmCloseTaskData: Nullable<ConfirmCloseTaskData>,
    confirmRejectTaskData: Nullable<ConfirmRejectTaskData>,
    private inFinalState: boolean = false
  ) {
    this.initializeExtraData(closeTaskData, rejectTaskData, confirmCloseTaskData, confirmRejectTaskData);
    this.initializePath();
    this.prepareSearchableText();

    // Precompute immutable sort keys once. Status-derived ordering is resolved live via
    // sortStatusRank because the task status can change at runtime (start/close/reject).
    // The painted day as a plain number (20260704), so two tasks showing the same day compare equal
    // whatever zone each is rendered in. Midnight as an instant would not: midnight in Madrid and
    // midnight in Lisbon are an hour apart, and the Madrid task would win every tie.
    this.sortExpectedDay = Number(this.date.expected.ymd.replace(/-/g, ''));
    // Within the day, the real chronological order: the instant the window opens.
    this.sortExpectedStart = Number(this.date.range.from.timestamp);
    // Priority id 1 = highest urgency; tasks without priority sort last.
    this.sortPriority = this.priority?.sortRank ?? Number.MAX_SAFE_INTEGER;
    // Immediate tasks first, planned tasks afterwards.
    this.sortImmediateRank = this.isPlanned() ? 1 : 0;
  }

  // Status ordering used by sorting: "realizada o rechazada" first, then "en curso", then "pendientes".
  get sortStatusRank(): number {
    if (this.isClosed() || this.isConfirmClosed() || this.isRejected() || this.isConfirmRejected()) {
      return 0;
    }

    if (this.isInProgress()) {
      return 1;
    }

    return 2;
  }

  hasDescriptionFiles(): boolean {
    return this.descriptionFiles.length > 0;
  }

  changeObservationsForClient(observations: Nullable<string>) {
    this.observationsForClient = observations;
    // overwrites also: close task data, confirm close task data, reject data and confirm reject data
    this.closeTaskData.observations = observations;
    this.confirmCloseTaskData.observationsForClient = observations;
    this.rejectTaskData.observations = observations;
    this.confirmRejectTaskData.observationsForClient = observations;
  }

  static fromPrimitives(primitives: TaskPrimitives): Task {
    const closeTaskData: CloseTaskData = {
      predefinedObservations: null,
      resolutions: null,
      observations: null,
      emails: null,
      signatureType: null,
      signature: null,
    };

    if (primitives.close_task_data) {
      closeTaskData.predefinedObservations = primitives.close_task_data.predefinedObservations;
      closeTaskData.resolutions = primitives.close_task_data.resolutions;
      closeTaskData.observations = primitives.close_task_data.observations;
      closeTaskData.emails = primitives.close_task_data.emails;
      closeTaskData.signatureType = primitives.close_task_data.signatureType;

      if (primitives.close_task_data.signature) {
        closeTaskData.signature = new LocalAttachment(
          primitives.close_task_data.signature.id,
          primitives.close_task_data.signature.filename,
          primitives.close_task_data.signature.path,
          primitives.close_task_data.signature.size
        );
      }
    }

    const rejectTaskData: RejectTaskData = {
      predefinedObservations: null,
      resolutions: null,
      observations: null,
    };
    if (primitives.reject_task_data) {
      rejectTaskData.predefinedObservations = primitives.reject_task_data.predefinedObservations;
      rejectTaskData.resolutions = primitives.reject_task_data.resolutions;
      rejectTaskData.observations = primitives.reject_task_data.observations;
    }

    const confirmCloseTaskData: ConfirmCloseTaskData = {
      resolution: null,
      observationsForClient: null,
    };
    if (primitives.confirm_close_task_data) {
      confirmCloseTaskData.resolution = primitives.confirm_close_task_data.resolution;
      confirmCloseTaskData.observationsForClient = primitives.confirm_close_task_data.observations_for_client;
    }

    const confirmRejectTaskData: ConfirmRejectTaskData = {
      resolution: null,
      observationsForClient: null,
    };
    if (primitives.confirm_reject_task_data) {
      confirmRejectTaskData.resolution = primitives.confirm_reject_task_data.resolution;
      confirmRejectTaskData.observationsForClient = primitives.confirm_reject_task_data.observations_for_client;
    }

    const descriptionFiles = primitives.description_files ?? [];
    return new Task(
      primitives.id,
      TaskStatus.fromPrimitives(primitives.status),
      primitives.subject,
      primitives.description,
      descriptionFiles,
      WorkType.fromPrimitives(primitives.work_type),
      primitives.task_type,
      primitives.priority ? Priority.fromPrimitives(primitives.priority) : null,
      primitives.route ?? null,
      TaskAmbit.fromPrimitives(primitives.ambit),
      TaskDate.fromPrimitives({
        expected: primitives.date.expected,
        from: primitives.date.from,
        to: primitives.date.to,
        timezone: primitives.date.timezone,
      }),
      primitives.assignee,
      primitives.must_be_modified_by_assignee,
      primitives.location,
      primitives.frequency,
      Resolution.fromPrimitives(primitives.resolution ?? null),
      LocalDate.fromString(primitives.started_at),
      LocalDate.fromString(primitives.updated_at),
      LocalDate.fromString(primitives.closed_at),
      LocalDate.fromString(primitives.confirm_closed_at),
      LocalDate.fromString(primitives.rejected_at),
      LocalDate.fromString(primitives.confirm_rejected_at),
      primitives.attachments?.map(
        (attachment) => new LocalFileAttachment(attachment.id, attachment.filename, attachment.path, attachment.size)
      ) ?? [],
      primitives.display_service_address_in_task,
      primitives.display_postal_address_in_immediate_task,
      primitives.display_postal_address_in_planned_task,
      primitives.display_ambit_in_immediate_task,
      primitives.display_ambit_in_planned_task,
      primitives.display_description_in_immediate_task,
      primitives.display_description_in_planned_task,
      primitives.display_inline_observations_for_client ?? false,
      closeTaskData,
      rejectTaskData,
      LocalDate.fromString(primitives.created_at),
      primitives.observations_for_client ?? null,
      confirmCloseTaskData,
      confirmRejectTaskData,
      primitives.in_final_state
    );
  }

  publishTaskHasChanged() {
    this.taskHasChanged$.next();
  }

  public isPlanned(): boolean {
    return this.workType.isPlanned();
  }

  public isImmediate(): boolean {
    return this.workType.isImmediate();
  }

  public isPending() {
    return this.status.isPending();
  }

  public isInProgress() {
    return this.status.isInProgress();
  }

  public isClosed() {
    return this.status.isClosed();
  }

  public isConfirmClosed() {
    return this.status.isClosedConfirmed();
  }

  public isRejected() {
    return this.status.isRejected();
  }

  public isConfirmRejected() {
    return this.status.isRejectedConfirmed();
  }

  start() {
    if (this.isInProgress()) {
      return;
    }

    this.storePreviousStatus();
    this.status = TaskStatus.inProgress();
    if (!this.startedAt) {
      this.startedAt = LocalDate.now();
    }
    this.touch();
  }

  stop() {
    if (this.isPending()) {
      return;
    }

    this.storePreviousStatus();
    this.status = TaskStatus.pending();
    this.touch();
  }

  close() {
    // this.closing = true;
    this.storePreviousStatus();
    this.status = TaskStatus.closed();
    this.closedAt = LocalDate.now();
    this.touch();
  }

  confirmClose() {
    this.storePreviousStatus();
    this.status = TaskStatus.closedConfirmed();
    this.confirmClosedAt = LocalDate.now();
    this.touch();
  }

  toPrimitives(): TaskPrimitives {
    const closeTaskDataPrimitives = {
      predefinedObservations: null,
      resolutions: null,
      observations: null,
      emails: null,
      signatureType: null,
      signature: null,
    };

    const closeTaskData = this.closeTaskData;
    if (closeTaskData) {
      closeTaskDataPrimitives.predefinedObservations = closeTaskData.predefinedObservations;
      closeTaskDataPrimitives.resolutions = closeTaskData.resolutions;
      closeTaskDataPrimitives.observations = closeTaskData.observations;
      closeTaskDataPrimitives.emails = closeTaskData.emails;
      closeTaskDataPrimitives.signatureType = closeTaskData.signatureType;
      closeTaskDataPrimitives.signature = closeTaskData.signature ? closeTaskData.signature.toPrimitives() : null;
    }

    const confirmCloseTaskDataPrimitives: ConfirmCloseTaskDataPrimitives = {
      resolution: null,
      observations_for_client: null,
    };
    if (this.confirmCloseTaskData) {
      confirmCloseTaskDataPrimitives.resolution = this.confirmCloseTaskData.resolution;
      confirmCloseTaskDataPrimitives.observations_for_client = this.confirmCloseTaskData.observationsForClient;
    }

    const confirmRejectTaskDataPrimitives: ConfirmRejectTaskDataPrimitives = {
      resolution: null,
      observations_for_client: null,
    };
    if (this.confirmRejectTaskData) {
      confirmRejectTaskDataPrimitives.resolution = this.confirmRejectTaskData.resolution;
      confirmRejectTaskDataPrimitives.observations_for_client = this.confirmRejectTaskData.observationsForClient;
    }

    return {
      id: this.id,
      status: this.status.toPrimitives(),
      in_final_state: this.inFinalState,
      subject: this.subject,
      description: this.description,
      description_files: this.descriptionFiles,
      work_type: this.workType.toPrimitives(),
      ambit: this.ambit.toPrimitives(),
      assignee: this.assignee,
      must_be_modified_by_assignee: this.mustBeModifiedByAssignee,
      location: this.location,
      frequency: this.frequency,
      resolution: this.resolution,
      date: this.date.toPrimitives(),
      created_at: this.createdAt?.atom ?? null,
      started_at: this.startedAt?.atom ?? null,
      updated_at: this.updatedAt?.atom ?? null,
      closed_at: this.closedAt?.atom ?? null,
      confirm_closed_at: this.confirmClosedAt?.atom ?? null,
      rejected_at: this.rejectedAt?.atom ?? null,
      confirm_rejected_at: this.confirmRejectedAt?.atom ?? null,
      attachments: this.attachments.map((attachment) => attachment.toPrimitives()),
      display_service_address_in_task: this.displayServiceAddressInTask,
      display_postal_address_in_immediate_task: this.displayPostalAddressInImmediateTask,
      display_postal_address_in_planned_task: this.displayPostalAddressInPlannedTask,
      display_ambit_in_immediate_task: this.displayAmbitInImmediateTask,
      display_ambit_in_planned_task: this.displayAmbitInPlannedTask,
      display_description_in_immediate_task: this.displayDescriptionInImmediateTask,
      display_description_in_planned_task: this.displayDescriptionInPlannedTask,
      display_inline_observations_for_client: this.displayInlineObservationForClient,
      close_task_data: closeTaskDataPrimitives,
      reject_task_data: this.rejectTaskData,
      observations_for_client: this.observationsForClient,
      confirm_close_task_data: confirmCloseTaskDataPrimitives,
      confirm_reject_task_data: confirmRejectTaskDataPrimitives,
      task_type: this.taskType,
      priority: this.priority?.toPrimitives() ?? null,
      route: this.route,
    };
  }

  inRange(range: DateRange): boolean {
    if (range === null) {
      return false;
    }

    // from now on, it is always filtered by valid date
    return this.date.range.intersects(range);
  }

  deadlineStatus(today: LocalDate): DeadlineFilter {
    if (today.isBefore(this.date.range.from)) {
      return DeadlineFilter.BEFORE;
    }

    if (today.isAfter(this.date.range.to)) {
      return DeadlineFilter.AFTER;
    }

    return DeadlineFilter.ON_TIME;
  }

  updateCloseTaskData(data: CloseTaskData) {
    this.attachments = [...(data.attachments ?? [])];
    this.closeTaskData = this.cloneCloseTaskData(data);
    if (this.closeTaskData.observations) {
      this.changeObservationsForClient(this.closeTaskData.observations);
    }
  }

  updateCloseResolution(resolution: Nullable<string>): void {
    this.closeTaskData.resolutions = resolution;
  }

  getCloseTaskData(): CloseTaskData {
    return {
      ...this.cloneCloseTaskData(this.closeTaskData),
      attachments: [...this.attachments],
    };
  }

  updateRejectTaskData(data: RejectTaskData) {
    this.attachments = [...(data.attachments ?? [])];
    this.rejectTaskData = this.cloneRejectTaskData(data);
    if (this.rejectTaskData.observations) {
      this.changeObservationsForClient(this.rejectTaskData.observations);
    }
  }

  updateRejectResolution(resolution: Nullable<string>): void {
    this.rejectTaskData.resolutions = resolution;
  }

  getRejectTaskData(): RejectTaskData {
    return {
      ...this.cloneRejectTaskData(this.rejectTaskData),
      attachments: [...this.attachments],
    };
  }

  getAttachmentsDirectory(): string {
    return this.id;
  }

  fulfillsText(substringsToSearch: string[]): boolean {
    return substringsToSearch.every((subcadena) => this.searchableText.includes(subcadena.toLowerCase()));
  }

  fulfillsAmbitId(id: string): boolean {
    return this.pathIds.includes(id);
  }

  expectedDate(expectedDateString: string = ''): string {
    return DateHelper.expectedDate(this.date, expectedDateString);
  }

  /**
   * The zone to show beside the hours, or null when there is nothing worth saying: the card prints no
   * hour at all, or the site keeps the same clock as the device.
   */
  timezoneHint(): Nullable<TimezoneHint> {
    if (!this.date.showsHours()) {
      return null;
    }

    return TimezoneHint.forDate(this.date.range.from);
  }

  hasGeolocationDefined(): boolean {
    return (
      this.location.geolocation !== null &&
      this.location.geolocation.longitude !== null &&
      this.location.geolocation.latitude !== null
    );
  }

  reject() {
    this.storePreviousStatus();
    this.status = TaskStatus.rejected();
    if (!this.rejectedAt) {
      this.rejectedAt = LocalDate.now();
    }
    this.touch();
  }

  confirmReject() {
    this.storePreviousStatus();
    this.status = TaskStatus.rejectedConfirmed();
    if (!this.confirmRejectedAt) {
      this.confirmRejectedAt = LocalDate.now();
    }
    this.touch();
  }

  storePreviousStatus() {
    this.previousStatus = this.status;
  }

  rollback() {
    if (this.isClosed()) {
      this.closedAt = null;
    } else if (this.isInProgress()) {
      this.startedAt = null;
    } else if (this.isRejected()) {
      this.rejectedAt = null;
    } else if (this.isConfirmClosed()) {
      this.confirmClosedAt = null;
    } else if (this.isConfirmRejected()) {
      this.confirmRejectedAt = null;
    } else if (this.isPending()) {
      // nothing to do
    }
    this.status = this.previousStatus;
    this.touch();
  }

  belongsTo(user: UserInfo): boolean {
    if (this.assignee === null) {
      return false;
    }

    // first, search by username
    if (this.assignee.username !== null) {
      const taskUsername = this.assignee.username?.toLowerCase() ?? null;
      const username = user.username?.toLowerCase() ?? null;
      if (taskUsername && username && taskUsername === username) {
        return true;
      }
    }

    // as fallback, search by email
    // TODO: Store searchable email (in both task and user)
    const taskEmail = this.assignee.email?.toLowerCase() ?? null;
    const userEmail = user.email?.toLowerCase() ?? null;
    return taskEmail && userEmail && taskEmail === userEmail;
  }

  canBeModifiedBy(user: UserInfo): boolean {
    if (this.assignee === null) {
      return true;
    }

    return this.belongsTo(user);
  }

  canBeActedOnBy(user: UserInfo): boolean {
    return !this.mustBeModifiedByAssignee || this.canBeModifiedBy(user);
  }

  changeId(newTaskId: string) {
    this.id = newTaskId;
  }

  isLocal(): boolean {
    return this.id.startsWith(Task.LOCAL_ID_PREFIX);
  }

  fulfillsAmbit(ambit: Ambit) {
    const codeToMatch = ambit.code;
    let match = false;
    if (codeToMatch !== null) {
      if (typeof this.ambit.value.place.code === 'string') {
        match = this.ambit.value.place.code === codeToMatch;
      } else {
        match = this.ambit.value.place.code.includes(codeToMatch);
      }
    }

    if (!match) {
      // if code is null, we check with ambit.id
      match = this.fulfillsAmbitId(ambit.id);
    }

    return match;
  }

  updateConfirmCloseTaskData(data?: ConfirmCloseTaskData) {
    this.confirmCloseTaskData = data ? this.cloneConfirmCloseTaskData(data) : this.createEmptyConfirmCloseTaskData();
  }

  getConfirmCloseTaskData(): ConfirmCloseTaskData {
    return this.cloneConfirmCloseTaskData(this.confirmCloseTaskData);
  }

  updateConfirmRejectTaskData(data?: ConfirmRejectTaskData) {
    this.confirmRejectTaskData = data ? this.cloneConfirmRejectTaskData(data) : this.createEmptyConfirmRejectTaskData();
  }

  getConfirmRejectTaskData(): ConfirmRejectTaskData {
    return this.cloneConfirmRejectTaskData(this.confirmRejectTaskData);
  }

  isInFinalState(): boolean {
    return this.inFinalState;
  }

  markAsFinalState(): void {
    this.inFinalState = true;
  }

  private touch(): void {
    this.updatedAt = LocalDate.now();
  }

  private cloneCloseTaskData(data: CloseTaskData): CloseTaskData {
    return {
      predefinedObservations: data.predefinedObservations ?? null,
      resolutions: data.resolutions ?? null,
      observations: data.observations ?? null,
      emails: data.emails ?? null,
      signatureType: data.signatureType ?? null,
      signature: data.signature ?? null,
    };
  }

  private createEmptyCloseTaskData(): CloseTaskData {
    return {
      predefinedObservations: null,
      resolutions: null,
      observations: null,
      emails: null,
      signatureType: null,
      signature: null,
    };
  }

  private cloneRejectTaskData(data: RejectTaskData): RejectTaskData {
    return {
      predefinedObservations: data.predefinedObservations ?? null,
      resolutions: data.resolutions ?? null,
      observations: data.observations ?? null,
    };
  }

  private createEmptyRejectTaskData(): RejectTaskData {
    return {
      predefinedObservations: null,
      resolutions: null,
      observations: null,
    };
  }

  private cloneConfirmCloseTaskData(data: ConfirmCloseTaskData): ConfirmCloseTaskData {
    return {
      resolution: data.resolution ?? null,
      observationsForClient: data.observationsForClient ?? null,
    };
  }

  private createEmptyConfirmCloseTaskData(): ConfirmCloseTaskData {
    return {
      resolution: null,
      observationsForClient: null,
    };
  }

  private cloneConfirmRejectTaskData(data: ConfirmRejectTaskData): ConfirmRejectTaskData {
    return {
      resolution: data.resolution ?? null,
      observationsForClient: data.observationsForClient ?? null,
    };
  }

  private createEmptyConfirmRejectTaskData(): ConfirmRejectTaskData {
    return {
      resolution: null,
      observationsForClient: null,
    };
  }

  private prepareSearchableText() {
    const subject = this.taskType ? `${this.subject} | ${this.taskType.name}` : this.subject;
    const strings = [
      StringHelper.latinize(subject.toLowerCase()),
      StringHelper.latinize(this.expectedDate().toLowerCase()),
      StringHelper.latinize(this.frequency?.toLowerCase()),
    ];

    // We add to searchable text only if it is not hidden using displayAmbit… configuration.
    if (
      (this.isPlanned() && this.displayAmbitInPlannedTask) ||
      (this.isImmediate() && this.displayAmbitInImmediateTask)
    ) {
      strings.push(StringHelper.latinize(this.ambit.printableAmbit().toLowerCase()));
    }

    // We add to searchable text only if it is not hidden using displayDescription… configuration.
    if (
      (this.isPlanned() && this.displayDescriptionInPlannedTask) ||
      (this.isImmediate() && this.displayDescriptionInImmediateTask)
    ) {
      if (this.description !== null && this.description !== undefined) {
        strings.push(StringHelper.latinize(this.description.toLowerCase()));
      }
    }

    if (this.assignee !== null) {
      strings.push(StringHelper.latinize(this.assignee.name.toLowerCase()));
    }

    if (this.route !== null) {
      strings.push(StringHelper.latinize(this.route.name.toLowerCase()));
    }

    if (this.displayServiceAddressInTask) {
      strings.push(...[StringHelper.latinize(this.ambit.serviceAddressName().toLowerCase())]);

      if (this.location.full_address !== null) {
        // happens when task is created by user
        strings.push(
          ...[
            // We add to searchable text even if it's hidden using displayPostalAddress… configuration because
            // the link to the postal address will still be there.
            StringHelper.latinize(this.location.full_address.toLowerCase()),
          ]
        );
      }
    }

    this.searchableText = strings.join(' ');
  }

  private initializeExtraData(
    closeTaskData: Nullable<CloseTaskData>,
    rejectTaskData: Nullable<RejectTaskData>,
    confirmCloseTaskData: Nullable<ConfirmCloseTaskData>,
    confirmRejectTaskData: Nullable<ConfirmRejectTaskData>
  ) {
    this.closeTaskData = closeTaskData ? this.cloneCloseTaskData(closeTaskData) : this.createEmptyCloseTaskData();
    this.rejectTaskData = rejectTaskData ? this.cloneRejectTaskData(rejectTaskData) : this.createEmptyRejectTaskData();
    this.confirmCloseTaskData = confirmCloseTaskData
      ? this.cloneConfirmCloseTaskData(confirmCloseTaskData)
      : this.createEmptyConfirmCloseTaskData();
    this.confirmRejectTaskData = confirmRejectTaskData
      ? this.cloneConfirmRejectTaskData(confirmRejectTaskData)
      : this.createEmptyConfirmRejectTaskData();
  }

  private initializePath() {
    this.pathIds = this.ambit.pathIds();
  }

  serviceAddressId(): string {
    return this.ambit.serviceAddressId();
  }
}
